/**
 * Every effect, in one table.
 *
 * An entry names the effect, says where its trigger lives, how the trigger is
 * worked, how long the scene may run, and how to load it. Nothing else in the
 * feature carries a list of effects, so adding one is a row here and a file
 * in `scenes/`.
 *
 * The scene loaders are dynamic imports on purpose. An idle page must not
 * carry seventeen scenes in its bundle, and a page nobody triggers loads none
 * of them.
 */

import type { ComponentType } from "react";

import type { EffectId, RunContext } from "./run.ts";

/** Every scene is a component that takes the run and calls `finish`. */
export interface SceneProps {
  readonly ctx: RunContext;
}

export type SceneModule = { readonly default: ComponentType<SceneProps> };

/**
 * How a trigger is worked.
 *
 * `clicks` counts completed activations inside a window. `clicksThenHold`
 * adds a deliberate press-and-hold at the end. `parts` is a spatial order
 * across several named buttons. `clicksPauseClick` needs a gap of the stated
 * length before the last activation, which is the one sequence in the
 * catalogue that is about waiting rather than speed.
 *
 * A window starts at the first activation. The final action must land before
 * it expires.
 */
export type TriggerSpec =
  | { readonly kind: "clicks"; readonly count: number; readonly windowMs: number }
  | {
      readonly kind: "clicksThenHold";
      readonly count: number;
      readonly holdMs: number;
      readonly windowMs: number;
    }
  | {
      readonly kind: "parts";
      /** The part names, in the order they must be worked. */
      readonly order: readonly string[];
      readonly windowMs: number;
    }
  | {
      readonly kind: "clicksPauseClick";
      readonly count: number;
      readonly windowMs: number;
      readonly gapMinMs: number;
      readonly gapMaxMs: number;
    };

export interface EffectDefinition {
  readonly id: EffectId;
  /** Where the trigger lives, for the person reading the documentation. */
  readonly page: string;
  /**
   * The route prefixes the effect belongs to.
   *
   * The provider refuses to start a scene whose route the person has already
   * left, and cancels a running one when they leave. The trigger only exists
   * on its own page, so this is a second boundary rather than the first.
   */
  readonly routes: readonly string[];
  readonly trigger: TriggerSpec;
  /** The accessible name of the trigger, or of its first part. */
  readonly triggerLabel: string;
  /** What the scene is, in one short sentence, for the status announcement. */
  readonly announce: string;
  /** The scene's own budget. The watchdog is separate and always 8 seconds. */
  readonly durationMs: number;
  readonly load: () => Promise<SceneModule>;
}

/** Nothing may run longer than this, whatever a scene believes. */
export const WATCHDOG_MS = 8000;

/** After any scene ends or is cancelled, no scene starts for this long. */
export const COOLDOWN_MS = 60_000;

/** A scene that never finishes loading is abandoned after this. */
export const LOAD_TIMEOUT_MS = 6000;

export const EFFECTS: readonly EffectDefinition[] = [
  {
    id: "satellite-laser",
    page: "Monitoring",
    routes: ["/monitoring"],
    trigger: { kind: "clicks", count: 5, windowMs: 4000 },
    triggerLabel: "Satellite animation",
    announce: "A satellite animation is playing.",
    durationMs: 5500,
    load: () => import("./scenes/satellite-laser.tsx"),
  },
  {
    id: "black-hole",
    page: "Risks",
    routes: ["/risks"],
    trigger: { kind: "clicksThenHold", count: 3, holdMs: 2000, windowMs: 8000 },
    triggerLabel: "Black hole animation",
    announce: "A black hole animation is playing.",
    durationMs: 7000,
    load: () => import("./scenes/black-hole.tsx"),
  },
  {
    id: "asteroid",
    page: "Changes",
    routes: ["/changes"],
    trigger: { kind: "clicks", count: 5, windowMs: 4000 },
    triggerLabel: "Asteroid animation",
    announce: "An asteroid animation is playing.",
    durationMs: 5000,
    load: () => import("./scenes/asteroid.tsx"),
  },
  {
    id: "astronaut-bounce",
    page: "My work",
    routes: ["/"],
    trigger: { kind: "clicks", count: 3, windowMs: 3000 },
    triggerLabel: "Astronaut animation",
    announce: "An astronaut animation is playing.",
    durationMs: 7000,
    load: () => import("./scenes/astronaut-bounce.tsx"),
  },
  {
    id: "rover",
    page: "Inventories",
    routes: ["/inventories"],
    trigger: {
      kind: "parts",
      order: ["front", "rear", "front", "rear"],
      windowMs: 5000,
    },
    triggerLabel: "Rover front wheel",
    announce: "A rover animation is playing.",
    durationMs: 8000,
    load: () => import("./scenes/rover.tsx"),
  },
  {
    id: "rocket-launch",
    page: "Executive dashboard, header",
    routes: ["/dashboard"],
    trigger: { kind: "clicksThenHold", count: 3, holdMs: 2000, windowMs: 8000 },
    triggerLabel: "Rocket animation",
    announce: "A rocket launch animation is playing.",
    durationMs: 6000,
    load: () => import("./scenes/rocket-launch.tsx"),
  },
  {
    id: "wormhole",
    page: "Evidence",
    routes: ["/evidence"],
    trigger: { kind: "clicks", count: 4, windowMs: 4000 },
    triggerLabel: "Wormhole animation",
    announce: "A wormhole animation is playing.",
    durationMs: 6000,
    load: () => import("./scenes/wormhole.tsx"),
  },
  {
    id: "alien-inspection",
    page: "Assessments",
    routes: ["/assessments"],
    trigger: { kind: "clicks", count: 5, windowMs: 5000 },
    triggerLabel: "Saucer animation",
    announce: "A flying saucer animation is playing.",
    durationMs: 6000,
    load: () => import("./scenes/alien-inspection.tsx"),
  },
  {
    id: "lunar-footprints",
    page: "Executive dashboard, delivery plan tab",
    routes: ["/dashboard"],
    trigger: { kind: "clicks", count: 4, windowMs: 4000 },
    triggerLabel: "Moon animation",
    announce: "A moon animation is playing.",
    durationMs: 7000,
    load: () => import("./scenes/lunar-footprints.tsx"),
  },
  {
    id: "zero-gravity",
    page: "Documents",
    routes: ["/documents"],
    trigger: {
      kind: "clicksPauseClick",
      count: 3,
      windowMs: 3000,
      gapMinMs: 1000,
      gapMaxMs: 2000,
    },
    triggerLabel: "Zero gravity animation",
    announce: "A zero gravity animation is playing.",
    durationMs: 6000,
    load: () => import("./scenes/zero-gravity.tsx"),
  },
  {
    id: "constellation",
    page: "Scope",
    routes: ["/scope"],
    trigger: {
      kind: "parts",
      order: ["star-1", "star-2", "star-3", "star-4", "star-5"],
      windowMs: 6000,
    },
    triggerLabel: "Constellation star 1",
    announce: "A constellation animation is playing.",
    durationMs: 5000,
    load: () => import("./scenes/constellation.tsx"),
  },
  {
    id: "eclipse",
    page: "Reports",
    routes: ["/reports"],
    trigger: { kind: "clicksThenHold", count: 4, holdMs: 2000, windowMs: 8000 },
    triggerLabel: "Eclipse animation",
    announce: "An eclipse animation is playing.",
    durationMs: 6000,
    load: () => import("./scenes/eclipse.tsx"),
  },
  {
    id: "docking",
    page: "Approvals",
    routes: ["/approvals"],
    trigger: {
      kind: "parts",
      order: ["left", "right", "centre"],
      windowMs: 5000,
    },
    triggerLabel: "Station left panel",
    announce: "A docking animation is playing.",
    durationMs: 6000,
    load: () => import("./scenes/docking.tsx"),
  },
  {
    id: "space-janitor",
    page: "Settings",
    routes: ["/settings"],
    trigger: { kind: "clicks", count: 5, windowMs: 4000 },
    triggerLabel: "Robot animation",
    announce: "A robot animation is playing.",
    durationMs: 7000,
    load: () => import("./scenes/space-janitor.tsx"),
  },
  {
    id: "escape-pod",
    page: "System security plan builder",
    routes: ["/ssp"],
    trigger: { kind: "clicks", count: 4, windowMs: 4000 },
    triggerLabel: "Escape pod animation",
    announce: "An escape pod animation is playing.",
    durationMs: 7000,
    load: () => import("./scenes/escape-pod.tsx"),
  },
  {
    id: "comet",
    page: "Executive dashboard, secondary decoration",
    routes: ["/dashboard"],
    trigger: { kind: "clicks", count: 5, windowMs: 4000 },
    triggerLabel: "Comet animation",
    announce: "A comet animation is playing.",
    durationMs: 5000,
    load: () => import("./scenes/comet.tsx"),
  },
  {
    id: "space-duck",
    page: "Settings, About panel",
    routes: ["/settings"],
    trigger: { kind: "clicks", count: 6, windowMs: 5000 },
    triggerLabel: "Duck animation",
    announce: "A duck animation is playing.",
    durationMs: 6000,
    load: () => import("./scenes/space-duck.tsx"),
  },
];

const BY_ID = new Map(EFFECTS.map((entry) => [entry.id, entry]));

export function effect(id: EffectId): EffectDefinition {
  const found = BY_ID.get(id);
  if (found === undefined) {
    throw new Error(`No effect is registered as ${id}.`);
  }
  return found;
}

/** True when the effect belongs to the address the person is looking at. */
export function onRoute(definition: EffectDefinition, pathname: string): boolean {
  return definition.routes.some((route) =>
    route === "/" ? pathname === "/" : pathname === route || pathname.startsWith(`${route}/`),
  );
}

/**
 * The pages that never run a scene.
 *
 * Sign-in, the employee magic-link portal, the public trust page, and the
 * external assessor portal are other people's screens or serious ones. The
 * trigger is not rendered there and the provider refuses anyway.
 */
export const NO_EFFECT_PREFIXES: readonly string[] = [
  "/sign-in",
  "/employee",
  "/public-page",
  "/audit/accept",
  "/audit/portal",
  "/security",
];

export function effectsAllowedOn(pathname: string): boolean {
  return !NO_EFFECT_PREFIXES.some(
    (prefix) => pathname === prefix || pathname.startsWith(`${prefix}/`),
  );
}
